import { useEffect, useRef, useState } from 'react';
import { createChart, IChartApi, ISeriesApi, Time, CandlestickSeries, ColorType } from 'lightweight-charts';
import { ChartData } from '../types';

interface UseChartSimpleReturn {
  chart: IChartApi | null;
  candlestickSeries: ISeriesApi<'Candlestick'> | null;
  setChartData: (data: ChartData[]) => void;
  updateChartData: (data: ChartData[]) => void;
}

export const useChartSimple = (containerRef: React.RefObject<HTMLDivElement | null>, containerReady: boolean, symbol?: string): UseChartSimpleReturn => {
  const [chart, setChart] = useState<IChartApi | null>(null);
  const [candlestickSeries, setCandlestickSeries] = useState<ISeriesApi<'Candlestick'> | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const pendingDataRef = useRef<ChartData[] | null>(null);
  const isInitializedRef = useRef<boolean>(false);
  const lastDataSetRef = useRef<string | null>(null);
  const currentSymbolRef = useRef<string | null>(null);

  const cleanupChart = () => {
    console.log('=== cleanupChart called ===');
    if (chartRef.current) {
      console.log('Removing existing chart');
      chartRef.current.remove();
      chartRef.current = null;
      setChart(null);
    }
    if (candlestickSeriesRef.current) {
      candlestickSeriesRef.current = null;
      setCandlestickSeries(null);
    }
    isInitializedRef.current = false;
    pendingDataRef.current = null;
    currentSymbolRef.current = null;
  };

  const initChart = () => {
    console.log('=== initChart called ===');
    console.log('containerRef.current:', containerRef.current);
    console.log('Current symbol:', symbol);
    console.log('Previous symbol:', currentSymbolRef.current);
    
    if (!containerRef.current) {
      console.log('Container ref is null in initChart');
      return;
    }

    // If symbol changed, cleanup existing chart
    if (currentSymbolRef.current && currentSymbolRef.current !== symbol) {
      console.log('Symbol changed, cleaning up existing chart');
      cleanupChart();
    }

    // Check if chart already exists for the same symbol
    if (chartRef.current && currentSymbolRef.current === symbol) {
      console.log('Chart already exists for same symbol, skipping recreation');
      return;
    }

    const containerWidth = containerRef.current.clientWidth || 800;
    const containerHeight = containerRef.current.clientHeight || 400;
    
    console.log('Container dimensions:', { containerWidth, containerHeight });

    try {
      const chartConfig = {
        width: containerWidth,
        height: containerHeight,
        layout: {
          background: { type: ColorType.Solid, color: '#1e1e1e' },
          textColor: '#d1d4dc',
        },
        grid: {
          vertLines: { color: '#2b2b2b' },
          horzLines: { color: '#2b2b2b' },
        },
        crosshair: {
          mode: 1,
        },
        rightPriceScale: {
          borderColor: '#485c7b',
        },
        timeScale: {
          borderColor: '#485c7b',
          timeVisible: true,
          secondsVisible: false,
        },
      };

      console.log('Creating chart with config:', chartConfig);
      const newChart = createChart(containerRef.current, chartConfig);
      console.log('Chart created successfully:', newChart);
      chartRef.current = newChart;
      setChart(newChart);
      isInitializedRef.current = true;

      // Create candlestick series
      const newCandlestickSeries = newChart.addSeries(CandlestickSeries, {
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: false,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350',
      });

      console.log('Candlestick series created:', newCandlestickSeries);
      candlestickSeriesRef.current = newCandlestickSeries;
      setCandlestickSeries(newCandlestickSeries);

      // Update current symbol reference
      currentSymbolRef.current = symbol || null;

      // Set pending data if available
      if (pendingDataRef.current) {
        console.log('Setting pending data:', pendingDataRef.current.length, 'items');
        setChartData(pendingDataRef.current);
        pendingDataRef.current = null;
      }

      console.log('Chart initialization completed successfully');

    } catch (error) {
      console.error('Error creating chart:', error);
    }
  };

  useEffect(() => {
    console.log('=== useChartSimple useEffect triggered ===');
    console.log('containerRef.current:', containerRef.current);
    console.log('containerReady:', containerReady);
    console.log('symbol:', symbol);
    console.log('chartRef.current:', chartRef.current);
    console.log('isInitializedRef.current:', isInitializedRef.current);
    
    if (!containerRef.current) {
      console.log('Container ref is null, skipping chart creation');
      return;
    }

    // If containerReady is false but containerRef.current exists, wait a bit
    if (!containerReady) {
      console.log('Container not ready yet, waiting...');
      const timer = setTimeout(() => {
        if (containerRef.current && !chartRef.current) {
          console.log('Retrying chart creation...');
          initChart();
        }
      }, 200);
      return () => clearTimeout(timer);
    }

    console.log('Container element found:', {
      tagName: containerRef.current.tagName,
      className: containerRef.current.className,
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight
    });

    // Force dimensions if they are 0
    if (containerRef.current.clientWidth === 0 || containerRef.current.clientHeight === 0) {
      console.log('Container has no dimensions, setting minimum dimensions');
      containerRef.current.style.width = '100%';
      containerRef.current.style.height = '400px';
      containerRef.current.style.minHeight = '400px';
    }

    // Try to initialize chart immediately
    initChart();

    // Cleanup
    return () => {
      console.log('Cleaning up chart');
      cleanupChart();
    };
  }, [containerReady]);

  // Separate effect for symbol changes - optimized to prevent multiple re-renders
  useEffect(() => {
    if (symbol && currentSymbolRef.current && currentSymbolRef.current !== symbol) {
      console.log('Symbol changed from', currentSymbolRef.current, 'to', symbol);
      // Cleanup existing chart and reinitialize
      cleanupChart();
      if (containerRef.current && containerReady) {
        // Use requestAnimationFrame for smoother transition
        requestAnimationFrame(() => {
          initChart();
        });
      }
    }
  }, [symbol, containerReady]); // Add back containerReady dependency
  

  const setChartData = (data: ChartData[]) => {
    console.log('=== setChartData called ===');
    console.log('Data length:', data.length);
    console.log('candlestickSeriesRef.current:', candlestickSeriesRef.current);
    console.log('chartRef.current:', chartRef.current);
    
    if (data.length === 0) {
      console.log('No data to set');
      return;
    }
    
    if (!candlestickSeriesRef.current || !chartRef.current) {
      console.log('Chart or series not ready, storing data for later');
      pendingDataRef.current = data;
      return;
    }

    try {
      const candlestickData = data.map(item => {
        const timestamp = item.time; // item.time is already a Unix timestamp
        
        // Use original OHLC values as received from Nobitex
        const normalizedOpen = item.open;
        const normalizedHigh = item.high;
        const normalizedLow = item.low;
        const normalizedClose = item.close;
        
        console.log('Converting initial data:', {
          time: item.time,
          timestamp: timestamp,
          open: normalizedOpen,
          high: normalizedHigh,
          low: normalizedLow,
          close: normalizedClose
        });
        
        return {
          time: timestamp as Time,
          open: normalizedOpen,
          high: normalizedHigh,
          low: normalizedLow,
          close: normalizedClose,
        };
      });

      // Sort data by time in ascending order
      candlestickData.sort((a, b) => (a.time as number) - (b.time as number));

      console.log('Setting candlestick data:', candlestickData.length, 'items');
      console.log('First few times:', candlestickData.slice(0, 5).map(d => d.time));
      console.log('Last few times:', candlestickData.slice(-5).map(d => d.time));
      console.log('First few OHLC values:', candlestickData.slice(0, 3).map(d => ({
        time: d.time,
        open: d.open,
        high: d.high,
        low: d.low,
        close: d.close
      })));
      
      // Check if this is a timeframe change (same symbol, different data)
      const isTimeframeChange = currentSymbolRef.current === symbol && 
        lastDataSetRef.current !== JSON.stringify(candlestickData.slice(0, 5));
      
      if (isTimeframeChange) {
        console.log('Timeframe change detected - updating data without recreation');
      } else {
        console.log('Setting new chart data (initial load or symbol change)');
      }
      
      console.log('Data length:', candlestickData.length);
      console.log('First candle time:', candlestickData[0]?.time);
      console.log('Last candle time:', candlestickData[candlestickData.length - 1]?.time);
      
      // Set new data
      candlestickSeriesRef.current.setData(candlestickData);
      chartRef.current.timeScale().fitContent();
      
      // Update last dataset reference
      lastDataSetRef.current = JSON.stringify(candlestickData.slice(0, 5));
      
      console.log('Chart data set successfully');
    } catch (error) {
      console.error('Error setting chart data:', error);
    }
  };

  const updateChartData = (data: ChartData[]) => {
    console.log('=== updateChartData called ===');
    console.log('Data length:', data.length);
    console.log('Data:', data);
    
    if (data.length === 0) {
      console.log('No data to update');
      return;
    }
    
    if (!candlestickSeriesRef.current || !chartRef.current) {
      console.log('Chart or series not ready for update');
      return;
    }

    try {
      const candlestickData = data.map(item => {
        const timestamp = item.time; // item.time is already a Unix timestamp
        
        // Use original OHLC values as received from Nobitex
        const normalizedOpen = item.open;
        const normalizedHigh = item.high;
        const normalizedLow = item.low;
        const normalizedClose = item.close;
        
        console.log('Converting real-time data:', {
          time: item.time,
          timestamp: timestamp,
          open: normalizedOpen,
          high: normalizedHigh,
          low: normalizedLow,
          close: normalizedClose
        });
        
        return {
          time: timestamp as Time,
          open: normalizedOpen,
          high: normalizedHigh,
          low: normalizedLow,
          close: normalizedClose,
        };
      });

      // Sort data by time in ascending order
      candlestickData.sort((a, b) => (a.time as number) - (b.time as number));

      console.log('Updating chart with real-time data:', candlestickData.length, 'items');
      console.log('Candle data:', candlestickData);
      
      // Get current data to compare times
      const currentData = candlestickSeriesRef.current.data();
      const lastCurrentTime = currentData[currentData.length - 1]?.time;
      const lastNewTime = candlestickData[candlestickData.length - 1]?.time;
      
      console.log('Last current time:', lastCurrentTime);
      console.log('Last new time:', lastNewTime);
      
      if (lastCurrentTime && lastNewTime && (lastNewTime as number) === (lastCurrentTime as number)) {
        // Update existing candle
        const lastCandle = candlestickData[candlestickData.length - 1];
        console.log('Updating existing candle:', lastCandle);
        candlestickSeriesRef.current!.update(lastCandle);
        console.log('Successfully updated existing candle');
      } else {
        // Add new candle(s) - only add candles newer than the last one
        const newCandles = candlestickData.filter(candle => 
          !lastCurrentTime || (candle.time as number) > (lastCurrentTime as number)
        );
        
        console.log('Adding new candles:', newCandles.length);
        newCandles.forEach((candle, index) => {
          try {
            console.log(`Adding new candle ${index + 1}/${newCandles.length}:`, candle);
            candlestickSeriesRef.current!.update(candle);
            console.log(`Successfully added new candle ${index + 1}`);
          } catch (updateError) {
            console.error(`Error adding new candle ${index + 1}:`, updateError, candle);
          }
        });
      }
      
    } catch (error) {
      console.error('Error updating chart data:', error);
    }
  };

  return {
    chart,
    candlestickSeries,
    setChartData,
    updateChartData,
  };
};